import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { LangToggle } from "@/components/Navbar";
import { matrixStrings as ms } from "@/lib/matrixStrings";
import { MatrixEntry } from "@/components/matrix/MatrixEntry";
import { MatrixRadar } from "@/components/matrix/MatrixRadar";
import { MatrixContact } from "@/components/matrix/MatrixContact";
import { MatrixAnalysis } from "@/components/matrix/MatrixAnalysis";
import { MatrixBooking } from "@/components/matrix/MatrixBooking";
import { track } from "@/lib/analytics";

const useParticles = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let w, h, raf;
    const mouse = { x: -9999, y: -9999, vx: 0, vy: 0 };
    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    const onMove = (e) => {
      if (mouse.x > -999) {
        mouse.vx = e.clientX - mouse.x;
        mouse.vy = e.clientY - mouse.y;
      }
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    window.addEventListener("mousemove", onMove);

    const dots = Array.from({ length: 70 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.25,
      vy: (Math.random() - 0.5) * 0.25,
      bx: 0,
      by: 0,
      r: Math.random() * 1.6 + 0.4,
    }));

    const loop = () => {
      ctx.clearRect(0, 0, w, h);
      mouse.vx *= 0.92;
      mouse.vy *= 0.92;
      const speed = Math.min(Math.hypot(mouse.vx, mouse.vy), 40);

      for (const d of dots) {
        const dx = d.x - mouse.x;
        const dy = d.y - mouse.y;
        const dm = Math.hypot(dx, dy);
        if (dm < 150 && dm > 0.1) {
          const f = ((150 - dm) / 150) * (0.55 + speed * 0.03);
          d.bx += (dx / dm) * f;
          d.by += (dy / dm) * f;
        }
        d.bx *= 0.9;
        d.by *= 0.9;
        d.x += d.vx + d.bx;
        d.y += d.vy + d.by;
        if (d.x < 0 || d.x > w) d.vx *= -1;
        if (d.y < 0 || d.y > h) d.vy *= -1;

        const near = dm < 180;
        const glow = near ? 0.55 + speed * 0.012 : 0.28;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r * (near ? 2 : 1), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(45,212,191,${Math.min(glow, 0.9)})`;
        ctx.fill();
      }
      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const dist = Math.hypot(dots[i].x - dots[j].x, dots[i].y - dots[j].y);
          if (dist < 110) {
            const nearMouse =
              Math.hypot((dots[i].x + dots[j].x) / 2 - mouse.x, (dots[i].y + dots[j].y) / 2 - mouse.y) < 200;
            ctx.beginPath();
            ctx.moveTo(dots[i].x, dots[i].y);
            ctx.lineTo(dots[j].x, dots[j].y);
            ctx.strokeStyle = `rgba(45,212,191,${(nearMouse ? 0.22 : 0.08) * (1 - dist / 110)})`;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return ref;
};

export default function Matrix() {
  const { lang } = useLanguage();
  const [stage, setStage] = useState("entry");
  const [profile, setProfile] = useState(null);
  const [scores, setScores] = useState(null);
  const [pending, setPending] = useState(null);
  const [contact, setContact] = useState(null);
  const [autoSend, setAutoSend] = useState(false);
  const canvasRef = useParticles();

  const stages = { entry: 1, radar: 2, contact: 3, analysis: 4, booking: 5 };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#050B14] font-body text-white" data-testid="matrix-page">
      <canvas ref={canvasRef} className="pointer-events-none fixed inset-0 z-0" aria-hidden="true" />
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(#14B8A6 1px, transparent 1px), linear-gradient(90deg, #14B8A6 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
        aria-hidden="true"
      />

      <header className="relative z-10 flex items-center justify-between px-4 py-5 sm:px-8">
        <a href="/" data-testid="matrix-back-link" className="flex items-center gap-2 text-sm font-semibold text-slate-300 transition-colors hover:text-teal-300">
          <ArrowLeft size={16} /> {ms.back[lang]}
        </a>
        <div className="flex items-center gap-3">
          <span className="hidden font-mono text-[10px] font-bold tracking-[0.3em] text-teal-400/70 sm:block">{ms.tag[lang]}</span>
          <LangToggle className="!border-teal-400/30 !bg-white/5 [&_button]:!text-slate-300" />
        </div>
      </header>

      <div className="relative z-10 mx-auto flex max-w-7xl justify-center gap-3 px-4 pb-2 pt-4">
        {Object.values(stages).map((n) => (
          <span
            key={n}
            data-testid={`matrix-step-${n}`}
            className={`h-1 w-14 rounded-full transition-colors duration-500 ${
              stages[stage] >= n ? "bg-teal-400 shadow-[0_0_10px_rgba(45,212,191,0.7)]" : "bg-white/10"
            }`}
          />
        ))}
      </div>

      <main className="relative z-10 mx-auto max-w-7xl px-4 pb-24 pt-10 sm:px-6 lg:px-8">
        {profile && (
          <div className={stage === "radar" ? "" : "hidden"}>
            <MatrixRadar
              lang={lang}
              clientType={profile?.clientType || "osoba"}
              onComplete={(s) => {
                setPending(s);
                setStage("contact");
              }}
            />
          </div>
        )}
        <AnimatePresence mode="wait">
          <motion.div key={stage} exit={{ opacity: 0, y: -24, transition: { duration: 0.4 } }}>
            {stage === "entry" && (
              <MatrixEntry
                lang={lang}
                onComplete={(p) => {
                  setProfile(p);
                  track("analysis_start", { clientType: p.clientType });
                  setStage("radar");
                }}
              />
            )}
            {stage === "contact" && pending && (
              <MatrixContact
                lang={lang}
                initialEmail={contact?.email || ""}
                initialPhone={contact?.phone || ""}
                onDone={(c) => {
                  if (c) {
                    setContact((prev) => ({ name: prev?.name || "", email: c.email || prev?.email || "", phone: c.phone || prev?.phone || "" }));
                    setAutoSend(Boolean(c.email));
                  } else {
                    setAutoSend(false);
                  }
                  setScores(pending);
                  track("analysis_complete", { vek: String(pending.vek) });
                  setStage("analysis");
                }}
              />
            )}
            {stage === "analysis" && scores && (
              <MatrixAnalysis lang={lang} scores={scores.scores} income={scores.income} vydaje={scores.vydaje} incomeExact={scores.incomeExact} vydajeExact={scores.vydajeExact} vek={scores.vek} rates={scores.rates} hasHypo={scores.hasHypo} hasUver={scores.hasUver} loanDetails={scores.loanDetails} answers={scores.answers} name={contact?.name || ""} contact={contact} autoSend={autoSend} onResultsSent={() => setAutoSend(false)} onContact={(n, e) => setContact((prev) => ({ name: n, email: e, phone: prev?.phone || "" }))} onBook={() => setStage("booking")} onBack={() => setStage("radar")} />
            )}
            {stage === "booking" && scores && (
              <MatrixBooking lang={lang} name={contact?.name || ""} email={contact?.email || ""} phone={contact?.phone || ""} scores={scores.scores} />
            )}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
